import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { User, Mail, Phone, Calendar, Save, Receipt, Shield, Download, Loader2, FileText, CreditCard, ClipboardList, Globe, CheckCircle, AlertTriangle, XCircle, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import TrustedContacts from '../../components/client/TrustedContacts';
import PartnerSharing from '../../components/client/PartnerSharing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientProfile = () => {
  const { partnerInfo, subdomain } = useOutletContext();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  
  // Profile state
  const [profile, setProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', mobile: '' });

  // Invoices state
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);

  // Consent History state
  const [consentLogs, setConsentLogs] = useState([]);
  const [consentLoading, setConsentLoading] = useState(true);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const clientToken = localStorage.getItem('clientToken');
  const axiosConfig = {
    headers: { Authorization: `Bearer ${clientToken}` }
  };

  // Fetch profile
  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/client/profile`, axiosConfig);
      setProfile(response.data);
      setFormData({
        name: response.data.name,
        email: response.data.email,
        mobile: response.data.mobile,
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile');
    }
  };

  // Fetch invoices
  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`, axiosConfig);
      const invoiceList = response.data.invoices || [];
      invoiceList.sort((a, b) => {
        const dateA = a.paid_at_raw || a.paid_at || a.created_at || '';
        const dateB = b.paid_at_raw || b.paid_at || b.created_at || '';
        return dateB.localeCompare(dateA);
      });
      setInvoices(invoiceList);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setInvoicesLoading(false);
    }
  };

  // Fetch consent logs
  const fetchConsentLogs = async () => {
    try {
      const response = await axios.get(`${API}/client/consent-log`, axiosConfig);
      setConsentLogs(response.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch consent log:', error);
    } finally {
      setConsentLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
    fetchInvoices();
    fetchConsentLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || formData.name.trim().length < 2) {
      toast.error('Name must be at least 2 characters');
      return;
    }

    if (!/^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/.test(formData.email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (!formData.mobile || !/^\d{10}$/.test(formData.mobile.trim().replace(/[\s\-\+]/g, '').slice(-10))) {
      toast.error('Mobile number must be 10 digits');
      return;
    }

    setIsLoading(true);

    try {
      await axios.put(`${API}/client/profile`, formData, axiosConfig);
      toast.success('Profile updated successfully!');
      localStorage.setItem('clientName', formData.name);
      setIsEditing(false);
      fetchProfile();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = Array.isArray(detail) ? detail.map(e => e.msg?.replace('Value error, ', '')).join(', ') : detail || 'Failed to update profile';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoice) => {
    if (!invoice.zoho_invoice_id) {
      toast.error('Invoice not available for download.');
      return;
    }
    
    setDownloadingInvoice(invoice.id);
    try {
      toast.loading('Downloading invoice...', { id: 'download' });
      
      const response = await axios.get(
        `${API}/invoice/${invoice.zoho_invoice_id}/pdf`,
        { ...axiosConfig, responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = invoice.zoho_invoice_number 
        ? `Invoice_${invoice.zoho_invoice_number}.pdf`
        : `Invoice_${invoice.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded!', { id: 'download' });
    } catch (error) {
      toast.error('Failed to download invoice', { id: 'download' });
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const handleRevokeConsent = async () => {
    if (confirmText !== 'DELETE MY DATA') {
      toast.error('Please type "DELETE MY DATA" to confirm');
      return;
    }

    setRevoking(true);
    try {
      await axios.post(`${API}/client/revoke-consent`, {}, axiosConfig);
      toast.success('Your consent has been revoked and all data has been deleted');
      
      localStorage.removeItem('clientToken');
      localStorage.removeItem('clientEmail');
      localStorage.removeItem('clientId');
      localStorage.removeItem('clientName');
      
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke consent');
      setRevoking(false);
    }
  };

  const formatConsentDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }) + ' IST';
  };

  const getPaymentTypeLabel = (type) => {
    switch (type) {
      case 'will_creation': return 'Will Creation';
      case 'will_renewal': return 'Will Renewal';
      default: return 'Payment';
    }
  };

  const getPaymentTypeColor = (type) => {
    switch (type) {
      case 'will_creation': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30';
      case 'will_renewal': return 'bg-amber-500/10 text-amber-400 border-amber-500/30';
      default: return 'bg-slate-500/10 text-slate-400 border-slate-500/30';
    }
  };

  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="client-profile">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Profile</h1>
          <p className="text-slate-400 mt-1">Manage your account, invoices, and consent</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-900/50 border border-slate-800 p-1 w-full sm:w-auto">
          <TabsTrigger 
            value="profile" 
            className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900 text-slate-400"
            data-testid="profile-tab"
          >
            <User className="w-4 h-4 mr-2" />
            Profile
          </TabsTrigger>
          <TabsTrigger 
            value="invoices" 
            className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900 text-slate-400"
            data-testid="invoices-tab"
          >
            <Receipt className="w-4 h-4 mr-2" />
            Invoices
          </TabsTrigger>
          <TabsTrigger 
            value="consent" 
            className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900 text-slate-400"
            data-testid="consent-tab"
          >
            <Shield className="w-4 h-4 mr-2" />
            Consent
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-6 mt-6">
          {/* Profile Card */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="border-b border-slate-800">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-3">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                    <span className="text-slate-900 font-bold text-2xl">
                      {profile.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">{profile.name}</h2>
                    <p className="text-slate-400 text-sm font-normal">
                      Client of {partnerInfo?.partner_name || 'Partner'}
                    </p>
                  </div>
                </CardTitle>
                {!isEditing && (
                  <Button
                    onClick={() => setIsEditing(true)}
                    variant="outline"
                    className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                    data-testid="edit-profile-btn"
                  >
                    Edit
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {isEditing ? (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="profile-form">
                  <div>
                    <Label htmlFor="name" className="text-slate-300 mb-2 block">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleFormChange}
                        className="pl-10 bg-slate-800/50 border-slate-700 text-white"
                        data-testid="profile-name-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-slate-300 mb-2 block">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        disabled
                        className="pl-10 bg-slate-800/50 border-slate-700 text-white disabled:opacity-50"
                        data-testid="profile-email-input"
                      />
                    </div>
                    <p className="text-slate-500 text-xs mt-1">Email cannot be changed</p>
                  </div>
                  <div>
                    <Label htmlFor="mobile" className="text-slate-300 mb-2 block">Mobile Number</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <Input
                        id="mobile"
                        name="mobile"
                        type="tel"
                        required
                        value={formData.mobile}
                        onChange={handleFormChange}
                        className="pl-10 bg-slate-800/50 border-slate-700 text-white"
                        data-testid="profile-mobile-input"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsEditing(false);
                        setFormData({ name: profile.name, email: profile.email, mobile: profile.mobile });
                      }}
                      className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                      data-testid="save-profile-btn"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {isLoading ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center gap-4 p-4 bg-slate-800/30 rounded-xl">
                    <div className="p-3 rounded-lg bg-slate-800">
                      <Mail className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Email</p>
                      <p className="text-white font-medium">{profile.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-slate-800/30 rounded-xl">
                    <div className="p-3 rounded-lg bg-slate-800">
                      <Phone className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Mobile</p>
                      <p className="text-white font-medium">{profile.mobile}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-slate-800/30 rounded-xl">
                    <div className="p-3 rounded-lg bg-slate-800">
                      <Calendar className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Member Since</p>
                      <p className="text-white font-medium">
                        {new Date(profile.created_at).toLocaleDateString('en-IN', {
                          day: 'numeric', month: 'long', year: 'numeric',
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-slate-800/30 rounded-xl">
                    <div className="p-3 rounded-lg bg-slate-800">
                      <User className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Advisor</p>
                      <p className="text-white font-medium">{partnerInfo?.partner_name || 'Partner'}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Partner Sharing Section */}
          <PartnerSharing />

          {/* Trusted Contacts Section */}
          <TrustedContacts />
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-6 mt-6">
          {invoicesLoading ? (
            <div className="min-h-[300px] flex flex-col items-center justify-center gap-4">
              <Loader2 className="w-12 h-12 text-amber-400 animate-spin" />
              <p className="text-slate-400">Loading invoices...</p>
            </div>
          ) : invoices.length === 0 ? (
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-12 text-center">
                <div className="flex flex-col items-center gap-4">
                  <div className="p-6 rounded-full bg-slate-800/50">
                    <Receipt className="w-16 h-16 text-slate-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">No Invoices Found</h2>
                  <p className="text-slate-400 max-w-md">
                    You do not have any invoices yet. Invoices are generated when you make a payment.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {invoices.map((invoice) => (
                <Card key={invoice.id} className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="p-3 rounded-xl bg-amber-500/10">
                          <Receipt className="w-6 h-6 text-amber-400" />
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold text-white">
                              ₹{invoice.amount?.toLocaleString('en-IN')}
                            </h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getPaymentTypeColor(invoice.payment_type)}`}>
                              {getPaymentTypeLabel(invoice.payment_type)}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-400">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {invoice.paid_at || 'N/A'}
                            </span>
                            {invoice.payment_id && (
                              <span className="flex items-center gap-1 font-mono text-xs">
                                <CreditCard className="w-4 h-4" />
                                {invoice.payment_id.substring(0, 20)}...
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-auto">
                        {invoice.zoho_invoice_id ? (
                          <Button
                            size="sm"
                            className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                            onClick={() => handleDownloadInvoice(invoice)}
                            disabled={downloadingInvoice === invoice.id}
                            data-testid={`download-invoice-${invoice.id}`}
                          >
                            {downloadingInvoice === invoice.id ? (
                              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Downloading...</>
                            ) : (
                              <><Download className="w-4 h-4 mr-2" />Download</>
                            )}
                          </Button>
                        ) : (
                          <span className="text-slate-500 text-sm">Invoice not available</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Consent History Tab */}
        <TabsContent value="consent" className="space-y-6 mt-6">
          {consentLoading ? (
            <div className="min-h-[300px] flex flex-col items-center justify-center gap-4">
              <Loader2 className="w-12 h-12 text-amber-400 animate-spin" />
              <p className="text-slate-400">Loading consent history...</p>
            </div>
          ) : (
            <>
              {/* Consent Logs */}
              <Card className="bg-slate-900/50 border-slate-800">
                <CardHeader className="border-b border-slate-800">
                  <CardTitle className="text-white flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-amber-400" />
                    Consent History
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {consentLogs.length === 0 ? (
                    <div className="text-center py-8">
                      <Shield className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                      <p className="text-slate-400">No consent records found</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {consentLogs.map((log, index) => (
                        <div key={index} className="flex items-start gap-4 p-4 bg-slate-800/30 rounded-xl">
                          <div className={`p-2 rounded-lg ${
                            log.action === 'consent_given' ? 'bg-emerald-500/10' : 'bg-red-500/10'
                          }`}>
                            {log.action === 'consent_given' ? (
                              <CheckCircle className="w-5 h-5 text-emerald-400" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-400" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className={`font-medium ${
                              log.action === 'consent_given' ? 'text-emerald-400' : 'text-red-400'
                            }`}>
                              {log.action === 'consent_given' ? 'Consent Given' : 'Consent Revoked'}
                            </p>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-400 mt-1">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {formatConsentDate(log.consent_date || log.timestamp)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Globe className="w-4 h-4" />
                                {log.ip_address || 'N/A'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Revoke Consent */}
              <Card className="bg-slate-900/50 border-slate-800 border-red-500/20">
                <CardHeader className="border-b border-slate-800">
                  <CardTitle className="text-white flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    Revoke Consent
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                    <p className="text-red-300 text-sm">
                      <strong>Warning:</strong> Revoking your consent will permanently delete all your data including wills, vault documents, and account information. This action cannot be undone.
                    </p>
                  </div>
                  <Button
                    onClick={() => setShowRevokeModal(true)}
                    variant="outline"
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    data-testid="revoke-consent-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Revoke My Consent & Delete Data
                  </Button>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Revoke Consent Modal */}
      {showRevokeModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 rounded-2xl max-w-md w-full shadow-2xl border border-slate-700 overflow-hidden">
            <div className="bg-red-500/10 px-6 py-4 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-xl">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Confirm Data Deletion</h2>
                  <p className="text-slate-400 text-sm">This action is irreversible</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <p className="text-slate-300 mb-4">
                Type <strong className="text-red-400">DELETE MY DATA</strong> to confirm:
              </p>
              <Input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE MY DATA"
                className="bg-slate-800/50 border-slate-700 text-white"
                data-testid="confirm-delete-input"
              />
            </div>
            <div className="bg-slate-800/50 px-6 py-4 border-t border-slate-700 flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRevokeModal(false);
                  setConfirmText('');
                }}
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRevokeConsent}
                disabled={confirmText !== 'DELETE MY DATA' || revoking}
                className="flex-1 bg-red-500 hover:bg-red-400 text-white font-semibold disabled:opacity-50"
                data-testid="confirm-revoke-btn"
              >
                {revoking ? 'Deleting...' : 'Delete All Data'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientProfile;
